import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SubscriberManager from './contacts/SubscriberManager';
import ListScoring from './contacts/ListScoring';
import SegmentAudit from './contacts/SegmentAudit';
import Utilities from './contacts/Utilities';
import { 
  Users, 
  Upload, 
  Search, 
  Filter, 
  Plus, 
  Calculator,
  Edit,
  Trash2,
  Database,
  FileText,
  RefreshCw,
  Save,
  X,
  Activity,
  Printer,
  Copy,
  CheckCheck,
  AlertTriangle,
  ClipboardCheck,
  Download,
  Wrench,
} from 'lucide-react';
import axios from 'axios';

interface Segment {
  segmentid: number;
  segmentname: string;
  searchinfo: string;
}

interface List {
  listid: number;
  name?: string;
  listname?: string;
  description?: string;
  createdate?: string;
  active?: number;
}

interface CustomField {
  fieldid: number;
  listid: number;
  name: string;
  fieldtype: string;
  required: number;
  defaultvalue: string;
}

interface SegmentRule {
  id: string;
  fieldId: number;
  fieldName: string;
  values: string[];
}

interface HealthCheckRow {
  countyState: string;
  count: number;
}

interface HealthCheckResult {
  segmentName: string;
  listId: number;
  breakdown: HealthCheckRow[];
  total: number;
}

interface SegmentSize {
  count: number;
  listId: number;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const Contacts: React.FC = () => {
  const { canAccess, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'segments' | 'lists' | 'import' | 'scoring' | 'audit' | 'utilities'>('overview');
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [lists, setLists] = useState<List[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [segmentSizes, setSegmentSizes] = useState<{ [key: number]: SegmentSize }>({});
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [calculatingSegment, setCalculatingSegment] = useState<number | null>(null);
  const [exportingSegmentId, setExportingSegmentId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedList, setSelectedList] = useState<List | null>(null);

  // Clean & Import (ZeroBounce) states
  const [showCleanImportModal, setShowCleanImportModal] = useState(false);
  const [cleanImportFile, setCleanImportFile] = useState<File | null>(null);
  const [cleanImportLoading, setCleanImportLoading] = useState(false);
  const [cleanImportReport, setCleanImportReport] = useState<null | {
    csvStartingTotal: number;
    duplicates: number;
    unsubscribed: number;
    suppressed: number;
    cleaned: number;
    importedTotal: number;
  }>(null);
  
  // Segment editing/creation states
  const [showSegmentEditor, setShowSegmentEditor] = useState(false);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [segmentFormData, setSegmentFormData] = useState({
    name: '',
    listId: 1,
    ruleType: 'or' as 'and' | 'or',
    rules: [] as SegmentRule[]
  });

  // Segment Health Check states
  const [healthCheckSegment, setHealthCheckSegment] = useState<Segment | null>(null);
  const [healthCheckData, setHealthCheckData] = useState<HealthCheckResult | null>(null);
  const [loadingHealthCheck, setLoadingHealthCheck] = useState(false);
  const [healthCheckCopied, setHealthCheckCopied] = useState(false);

  // Overview stats
  const [overviewStats, setOverviewStats] = useState({
    totalLists: 0,
    totalSegments: 0
  });

  useEffect(() => {
    fetchSegments();
    fetchLists();
    fetchCustomFields();
  }, []);

  useEffect(() => {
    // Update overview stats when segments and lists change
    setOverviewStats({
      totalLists: lists.length,
      totalSegments: segments.length
    });
  }, [segments, lists]);

  const fetchSegments = async () => {
    setLoadingSegments(true);
    try {
      const response = await axios.get('/api/interspire/segments');
      setSegments(response.data);
    } catch (error) {
      console.error('Error fetching segments:', error);
      alert('Failed to load segments. Please check your Interspire database connection.');
    } finally {
      setLoadingSegments(false);
    }
  };

  const fetchLists = async () => {
    setLoadingLists(true);
    try {
      const response = await axios.get('/api/interspire/lists');
      setLists(response.data);
    } catch (error) {
      console.error('Error fetching lists:', error);
      alert('Failed to load lists. Please check your Interspire database connection.');
    } finally {
      setLoadingLists(false);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get('/api/interspire/customfields');
      setCustomFields(response.data);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      // Don't show error for custom fields as it's not critical for basic functionality
    }
  };

  const fetchHealthCheck = async (segment: Segment) => {
    setHealthCheckSegment(segment);
    setHealthCheckData(null);
    setLoadingHealthCheck(true);
    try {
      const response = await axios.get(`/api/interspire/segments/${segment.segmentid}/health`);
      setHealthCheckData(response.data);
    } catch (error) {
      console.error('Error running health check:', error);
      alert('Failed to run health check. Please try again.');
      setHealthCheckSegment(null);
    } finally {
      setLoadingHealthCheck(false);
    }
  };

  const exportSegmentContacts = async (segment: Segment) => {
    setExportingSegmentId(segment.segmentid);
    try {
      const safeName = segment.segmentname.replace(/[^a-z0-9_-]/gi, '_');
      const res = await axios.get(
        `/api/interspire/segments/${segment.segmentid}/contacts/export?segmentName=${encodeURIComponent(safeName)}`,
        { responseType: 'blob', timeout: 0 }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}_segment_${segment.segmentid}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 502 || status === 504) {
        window.alert('Segment export timed out. Please try again — large segments now stream in chunks.');
      } else {
        window.alert('Segment export failed. Please try again.');
      }
    } finally {
      setExportingSegmentId(null);
    }
  };

  const closeHealthCheck = () => {
    setHealthCheckSegment(null);
    setHealthCheckData(null);
    setHealthCheckCopied(false);
  };

  const copyHealthCheck = () => {
    if (!healthCheckData) return;
    const lines = [
      `Segment Health Check: ${healthCheckData.segmentName}`,
      `List ID: ${healthCheckData.listId}`,
      '',
      'County, State\tActive Contacts',
      ...healthCheckData.breakdown.map(r => `${r.countyState}\t${r.count.toLocaleString()}`),
      '',
      `TOTAL\t${healthCheckData.total.toLocaleString()}`,
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    setHealthCheckCopied(true);
    setTimeout(() => setHealthCheckCopied(false), 2500);
  };

  const printHealthCheck = () => {
    if (!healthCheckData) return;
    const rows = healthCheckData.breakdown
      .map(r => `<tr><td style="padding:3px 14px;border-bottom:1px solid #e5e7eb">${escapeHtml(r.countyState)}</td><td style="padding:3px 14px;text-align:right;border-bottom:1px solid #e5e7eb;font-weight:500">${escapeHtml(String(r.count.toLocaleString()))}</td></tr>`)
      .join('');
    const locationCount = healthCheckData.breakdown.length;
    const html = `<!DOCTYPE html><html><head><title>Segment Health Check – ${healthCheckData.segmentName}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 40px; color: #111; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { font-size: 13px; color: #555; margin: 0 0 20px; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  thead th { background: #f3f4f6; padding: 7px 14px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: #6b7280; border-bottom: 2px solid #d1d5db; }
  thead th:last-child { text-align: right; }
  tfoot td { padding: 8px 14px; font-weight: 700; border-top: 2px solid #374151; background: #f3f4f6; font-size: 14px; }
  tfoot td:last-child { text-align: right; }
</style></head>
<body>
  <h1>Segment Health Check</h1>
  <p class="meta">${healthCheckData.segmentName}<br>List ID: ${healthCheckData.listId} &nbsp;·&nbsp; ${locationCount} location${locationCount !== 1 ? 's' : ''}</p>
  <table>
    <thead><tr><th>County, State</th><th style="text-align:right">Active Contacts</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td>TOTAL</td><td>${healthCheckData.total.toLocaleString()}</td></tr></tfoot>
  </table>
</body></html>`;
    const w = window.open('', '_blank', 'width=720,height=900');
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  };

  const calculateSegmentSize = async (segmentId: number) => {
    if (calculatingSegment) return; // Prevent multiple calculations at once
    
    setCalculatingSegment(segmentId);
    try {
      const response = await axios.get(`/api/interspire/segments/${segmentId}/size`);
      setSegmentSizes(prev => ({
        ...prev,
        [segmentId]: response.data
      }));
    } catch (error) {
      console.error('Error calculating segment size:', error);
      alert(`Failed to calculate segment size for segment ${segmentId}`);
    } finally {
      setCalculatingSegment(null);
    }
  };

  const openSegmentEditor = async (segment?: Segment) => {
    if (segment) {
      setEditingSegment(segment);
      
      try {
        // Fetch detailed segment data for editing
        const response = await axios.get(`/api/interspire/segments/${segment.segmentid}/details`);
        const editableData = response.data.editableData;
        setSegmentFormData({
          name: editableData.name || segment.segmentname,
          listId: editableData.listId ?? 1,
          ruleType: editableData.ruleType || 'or',
          rules: editableData.rules || []
        });
      } catch (error) {
        console.error('Error loading segment for editing:', error);
        // Fallback to basic data
        setSegmentFormData({
          name: segment.segmentname,
          listId: 1,
          ruleType: 'or',
          rules: []
        });
      }
    } else {
      setEditingSegment(null);
      setSegmentFormData({
        name: '',
        listId: lists.length > 0 ? lists[0].listid : 1,
        ruleType: 'or',
        rules: []
      });
    }
    setShowSegmentEditor(true);
  };

  const closeSegmentEditor = () => {
    setShowSegmentEditor(false);
    setEditingSegment(null);
    setSegmentFormData({
      name: '',
      listId: 1,
      ruleType: 'or',
      rules: []
    });
  };

  const addNewRule = () => {
    const newRule: SegmentRule = {
      id: Date.now().toString(),
      fieldId: customFields.length > 0 ? customFields[0].fieldid : 9,
      fieldName: customFields.length > 0 ? customFields[0].name : 'State',
      values: []
    };
    setSegmentFormData(prev => ({
      ...prev,
      rules: [...prev.rules, newRule]
    }));
  };

  const updateRule = (ruleId: string, field: string, value: any) => {
    setSegmentFormData(prev => ({
      ...prev,
      rules: prev.rules.map(rule => {
        if (rule.id === ruleId) {
          if (field === 'fieldId') {
            const selectedField = customFields.find(f => f.fieldid === value);
            return {
              ...rule,
              fieldId: value,
              fieldName: selectedField?.name || 'Unknown Field'
            };
          } else if (field === 'values') {
            // Parse values from textarea (one per line)
            const values = value.split('\n').filter((v: string) => v.trim() !== '');
            return { ...rule, values };
          }
        }
        return rule;
      })
    }));
  };

  const removeRule = (ruleId: string) => {
    setSegmentFormData(prev => ({
      ...prev,
      rules: prev.rules.filter(rule => rule.id !== ruleId)
    }));
  };

  const saveSegment = async () => {
    try {
      // Validate form data
      if (!segmentFormData.name.trim()) {
        alert('Please enter a segment name');
        return;
      }

      if (segmentFormData.rules.length === 0) {
        alert('Please add at least one rule');
        return;
      }

      // Validate that all rules have values
      for (const rule of segmentFormData.rules) {
        if (rule.values.length === 0) {
          alert('All rules must have at least one value');
          return;
        }
      }

      const requestData = {
        name: segmentFormData.name.trim(),
        listId: segmentFormData.listId,
        rules: segmentFormData.rules.map(rule => ({
          fieldId: rule.fieldId,
          values: rule.values.filter(v => v.trim() !== '')
        })),
        ruleType: segmentFormData.ruleType
      };

      console.log('Saving segment with data:', requestData);

      let response;
      if (editingSegment) {
        // Update existing segment
        response = await axios.put(`/api/interspire/segments/${editingSegment.segmentid}`, requestData);
      } else {
        // Create new segment
        response = await axios.post('/api/interspire/segments', requestData);
      }

      console.log('Segment save response:', response.data);

      if (response.data.success) {
        alert(response.data.message);
        closeSegmentEditor();
        // Refresh the segments list
        await fetchSegments();
        // Remount Audit tab so verification badges reload after edit invalidates stamp
        if (activeTab === 'audit') setAuditRefreshKey((k) => k + 1);
      } else {
        alert('Failed to save segment: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving segment:', error);
      let errorMessage = 'Failed to save segment';
      
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        errorMessage += ': ' + error.response.data.error;
        if (error.response.data.details) {
          errorMessage += ' (' + error.response.data.details + ')';
        }
      }
      
      alert(errorMessage);
    }
  };

  const deleteSegment = async (segment: Segment) => {
    if (!window.confirm(`Are you sure you want to delete the segment "${segment.segmentname}"? This action cannot be undone.`)) {
      return;
    }

    try {
      console.log('Deleting segment:', segment.segmentid);
      
      const response = await axios.delete(`/api/interspire/segments/${segment.segmentid}`);
      
      console.log('Segment delete response:', response.data);

      if (response.data.success) {
        alert(response.data.message);
        // Refresh the segments list
        await fetchSegments();
        // Clear any cached segment size
        setSegmentSizes(prev => {
          const updated = { ...prev };
          delete updated[segment.segmentid];
          return updated;
        });
      } else {
        alert('Failed to delete segment: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting segment:', error);
      let errorMessage = 'Failed to delete segment';
      
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        errorMessage += ': ' + error.response.data.error;
        if (error.response.data.details) {
          errorMessage += ' (' + error.response.data.details + ')';
        }
      }
      
      alert(errorMessage);
    }
  };

  const filteredSegments = segments.filter(segment =>
    segment.segmentname.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredLists = lists.filter(list => {
    const name = list.name || list.listname || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Cards - Removed Calculated Contacts and Active Segments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Database className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Lists</dt>
                <dd className="text-lg font-medium text-gray-900">{overviewStats.totalLists}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Filter className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Segments</dt>
                <dd className="text-lg font-medium text-gray-900">{overviewStats.totalSegments}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {canAccess('contactsQuickActions') && (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => { setActiveTab('import'); setSearchQuery(''); }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import Contacts
            </button>
            
            {user?.role === 'admin' && (
            <button
              onClick={() => setShowCleanImportModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FileText className="h-4 w-4 mr-2" />
              Clean & Import
            </button>
            )}

            <button
              onClick={() => { setActiveTab('segments'); setSearchQuery(''); }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Filter className="h-4 w-4 mr-2" />
              Manage Segments
            </button>
            
            <button
              onClick={() => { setActiveTab('lists'); setSearchQuery(''); setSelectedList(null); }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Database className="h-4 w-4 mr-2" />
              View Lists
            </button>

            {canAccess('alumniImport') && (
              <button
                onClick={() => navigate('/alumni-import')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Users className="h-4 w-4 mr-2" />
                Alumni Import
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Recent Activity Preview */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Recent Segments</h3>
          <div className="space-y-3">
            {segments.slice(0, 5).map((segment) => (
              <div key={segment.segmentid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">{segment.segmentname}</p>
                  <p className="text-xs text-gray-500">Segment ID: {segment.segmentid}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {segmentSizes[segment.segmentid] && (
                    <span className="text-sm text-gray-600">
                      {segmentSizes[segment.segmentid].count.toLocaleString()} contacts
                    </span>
                  )}
                  <button
                    onClick={() => calculateSegmentSize(segment.segmentid)}
                    disabled={calculatingSegment === segment.segmentid}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    {calculatingSegment === segment.segmentid ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Calculator className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSegments = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Segment Management</h2>
        <div className="flex space-x-4">
          {canAccess('contactsBuildSegment') && (
          <button 
            onClick={() => openSegmentEditor()}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Plus className="h-4 w-4 mr-2" />
            Build New Segment
          </button>
          )}
          <button
            onClick={fetchSegments}
            disabled={loadingSegments}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loadingSegments ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Clean & Import Modal */}
      {showCleanImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Clean & Import Contacts</h3>
              <button onClick={() => { if (!cleanImportLoading) { setShowCleanImportModal(false); setCleanImportFile(null); setCleanImportReport(null); } }} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">Upload a CSV file. We will remove duplicates, unsubscribed, and suppressed emails, validate with ZeroBounce, then import VALID and CATCH-ALL results.</p>

              <div>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setCleanImportFile(file);
                    setCleanImportReport(null);
                  }}
                  className="block w-full text-sm text-gray-700"
                  disabled={cleanImportLoading}
                />
                <p className="mt-1 text-xs text-gray-500">CSV must include an email column.</p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { if (!cleanImportLoading) { setShowCleanImportModal(false); setCleanImportFile(null); setCleanImportReport(null); } }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  disabled={cleanImportLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!cleanImportFile) {
                      alert('Please select a CSV file first.');
                      return;
                    }
                    try {
                      setCleanImportLoading(true);
                      setCleanImportReport(null);
                      const form = new FormData();
                      form.append('file', cleanImportFile);
                      const resp = await axios.post('/api/interspire/clean-import', form, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      if (resp.data?.success) {
                        setCleanImportReport(resp.data.report);
                      } else {
                        alert(resp.data?.error || 'Clean & Import failed');
                      }
                    } catch (error: any) {
                      const msg = error?.response?.data?.error || error?.message || 'Clean & Import failed';
                      alert(msg);
                    } finally {
                      setCleanImportLoading(false);
                    }
                  }}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${cleanImportLoading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                  disabled={cleanImportLoading}
                >
                  {cleanImportLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Run Clean & Import'
                  )}
                </button>
              </div>

              {cleanImportReport && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Report</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between"><span className="text-gray-600">CSV Starting Total</span><span className="font-medium">{cleanImportReport.csvStartingTotal.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Duplicates</span><span className="font-medium">{cleanImportReport.duplicates.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Unsubscribed</span><span className="font-medium">{cleanImportReport.unsubscribed.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Suppressed</span><span className="font-medium">{cleanImportReport.suppressed.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Cleaned</span><span className="font-medium">{cleanImportReport.cleaned.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Imported Total</span><span className="font-medium">{cleanImportReport.importedTotal.toLocaleString()}</span></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 max-w-lg">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search segments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Segments Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Segments ({filteredSegments.length})
          </h3>
        </div>
        <ul className="divide-y divide-gray-200">
          {loadingSegments ? (
            <li className="px-4 py-4 text-center text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading segments...
            </li>
          ) : filteredSegments.length === 0 ? (
            <li className="px-4 py-4 text-center text-gray-500">
              {searchQuery ? `No segments found matching "${searchQuery}"` : 'No segments available'}
            </li>
          ) : (
            filteredSegments.map((segment) => (
              <li key={segment.segmentid} className="px-4 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{segment.segmentname}</p>
                        <p className="text-sm text-gray-500">Segment ID: {segment.segmentid}</p>
                        {segmentSizes[segment.segmentid] && (
                          <p className="text-sm text-green-600 font-medium">
                            {segmentSizes[segment.segmentid].count.toLocaleString()} contacts 
                            (List ID: {segmentSizes[segment.segmentid].listId})
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => calculateSegmentSize(segment.segmentid)}
                      disabled={calculatingSegment === segment.segmentid}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900/40 dark:hover:bg-blue-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {calculatingSegment === segment.segmentid ? (
                        <>
                          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                          Calculating...
                        </>
                      ) : (
                        <>
                          <Calculator className="h-3 w-3 mr-1" />
                          Calculate
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => fetchHealthCheck(segment)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Activity className="h-3 w-3 mr-1" />
                      Health Check
                    </button>
                    {canAccess('contactsExportSegment') && (
                    <button
                      onClick={() => exportSegmentContacts(segment)}
                      disabled={exportingSegmentId === segment.segmentid}
                      title="Export confirmed active members as CSV"
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded bg-indigo-100 hover:bg-indigo-200 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-[#4a4e61]"
                    >
                      {exportingSegmentId === segment.segmentid ? (
                        <>
                          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                          Exporting…
                        </>
                      ) : (
                        <>
                          <Download className="h-3 w-3 mr-1" />
                          Export CSV
                        </>
                      )}
                    </button>
                    )}
                    {canAccess('contactsEditSegment') && (
                    <button 
                      onClick={() => openSegmentEditor(segment)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </button>
                    )}
                    {canAccess('contactsDeleteSegment') && (
                    <button 
                      onClick={() => deleteSegment(segment)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </button>
                    )}
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );

  const renderLists = () => {
    if (selectedList) {
      return (
        <SubscriberManager
          list={selectedList}
          customFields={customFields}
          onBack={() => setSelectedList(null)}
        />
      );
    }

    return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Contact Lists</h2>
        <button
          onClick={fetchLists}
          disabled={loadingLists}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loadingLists ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 max-w-lg">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search lists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Lists Table - Removed View Contacts button */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Lists ({filteredLists.length})
          </h3>
        </div>
        <ul className="divide-y divide-gray-200">
          {loadingLists ? (
            <li className="px-4 py-4 text-center text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading lists...
            </li>
          ) : filteredLists.length === 0 ? (
            <li className="px-4 py-4 text-center text-gray-500">
              {searchQuery ? `No lists found matching "${searchQuery}"` : 'No lists available'}
            </li>
          ) : (
            filteredLists.map((list) => (
              <li key={list.listid} className="px-4 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {list.name || list.listname || `List ${list.listid}`}
                        </p>
                        <p className="text-sm text-gray-500">List ID: {list.listid}</p>
                        {list.description && (
                          <p className="text-sm text-gray-600 mt-1">{list.description}</p>
                        )}
                        {list.createdate && (
                          <p className="text-xs text-gray-400 mt-1">
                            Created: {new Date(parseInt(list.createdate) * 1000).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {list.active !== undefined && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        list.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {list.active ? 'Active' : 'Inactive'}
                      </span>
                    )}
                    <button
                      onClick={() => setSelectedList(list)}
                      className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
                    >
                      <Users className="h-3.5 w-3.5 mr-1" />
                      View Contacts
                    </button>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
    );
  };

  const renderImport = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Contact Import</h2>
      </div>

      {/* CSV Import Only - Removed Database Import */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <Upload className="h-6 w-6 text-blue-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">CSV File Import</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Upload a CSV file with contact information to import into your lists.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target List
            </label>
            <select className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
              <option value="">Select a list...</option>
              {lists.map((list) => (
                <option key={list.listid} value={list.listid}>
                  {list.name || list.listname || `List ${list.listid}`}
                </option>
              ))}
            </select>
          </div>
          <button 
            onClick={() => alert('Import functionality is currently in beta and will be available soon!')}
            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import Contacts (Beta)
          </button>
          <p className="text-xs text-orange-600 text-center">
            This feature is currently in beta testing and will be available in a future update.
          </p>
        </div>
      </div>

      {/* Import History */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Import History</h3>
        </div>
        <div className="px-4 py-4">
          <div className="text-center text-gray-500 py-8">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No import history available</p>
            <p className="text-sm">Your import activities will appear here</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <Users className="h-8 w-8 text-blue-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contact Management</h1>
            <p className="text-gray-600">Manage your contacts, lists, and segments</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => { setActiveTab('overview'); setSearchQuery(''); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center">
              <Database className="h-4 w-4 mr-2" />
              Overview
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('segments'); setSearchQuery(''); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'segments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              Segments ({segments.length})
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('lists'); setSelectedList(null); setSearchQuery(''); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'lists'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center">
              <Database className="h-4 w-4 mr-2" />
              Lists ({lists.length})
            </div>
          </button>
          {canAccess('contactsImport') && (
          <button
            onClick={() => { setActiveTab('import'); setSearchQuery(''); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'import'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center">
              <Upload className="h-4 w-4 mr-2" />
              Import
            </div>
          </button>
          )}
          {canAccess('contactsListScoring') && (
          <button
            onClick={() => { setActiveTab('scoring'); setSearchQuery(''); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'scoring'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Segment Scoring
            </div>
          </button>
          )}
          {canAccess('contactsAuditSegments') && (
          <button
            onClick={() => { setActiveTab('audit'); setSearchQuery(''); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'audit'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center">
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Audit Segments
            </div>
          </button>
          )}
          {canAccess('contactsUtilities') && (
          <button
            onClick={() => { setActiveTab('utilities'); setSearchQuery(''); }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'utilities'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center">
              <Wrench className="h-4 w-4 mr-2" />
              Utilities
            </div>
          </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'segments' && renderSegments()}
      {activeTab === 'lists' && renderLists()}
      {activeTab === 'import' && canAccess('contactsImport') && renderImport()}
      {activeTab === 'scoring' && canAccess('contactsListScoring') && (
        <ListScoring segments={segments} lists={lists} />
      )}
      {activeTab === 'audit' && canAccess('contactsAuditSegments') && (
        <SegmentAudit
          key={auditRefreshKey}
          onEditSegment={(seg) => {
            const full = segments.find((s) => s.segmentid === seg.segmentid);
            openSegmentEditor(full ?? { segmentid: seg.segmentid, segmentname: seg.segmentname, searchinfo: '' });
          }}
        />
      )}
      {activeTab === 'utilities' && canAccess('contactsUtilities') && (
        <Utilities lists={lists} segments={segments} />
      )}

      {/* Segment Editor Modal — page-level so Audit (and other tabs) can open it without switching */}
      {showSegmentEditor && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingSegment ? `Edit Segment: ${editingSegment.segmentname}` : 'Create New Segment'}
              </h3>
              <button onClick={closeSegmentEditor} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Segment Name</label>
                  <input
                    type="text"
                    value={segmentFormData.name}
                    onChange={(e) => setSegmentFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter segment name..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Source List</label>
                  <select
                    value={segmentFormData.listId}
                    onChange={(e) => setSegmentFormData(prev => ({ ...prev, listId: parseInt(e.target.value) }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {lists.map(list => (
                      <option key={list.listid} value={list.listid}>
                        {list.name || list.listname || `List ${list.listid}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rule Type</label>
                  <select
                    value={segmentFormData.ruleType}
                    onChange={(e) => setSegmentFormData(prev => ({ ...prev, ruleType: e.target.value as 'and' | 'or' }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="or">OR (Any rule matches)</option>
                    <option value="and">AND (All rules must match)</option>
                  </select>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-medium text-gray-900">Segment Rules</h4>
                  <button
                    onClick={addNewRule}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Rule
                  </button>
                </div>

                <div className="space-y-4">
                  {segmentFormData.rules.map((rule, index) => (
                    <div key={rule.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <h5 className="text-sm font-medium text-gray-900">Rule {index + 1}</h5>
                        <button
                          onClick={() => removeRule(rule.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Field</label>
                          <select
                            value={rule.fieldId}
                            onChange={(e) => updateRule(rule.id, 'fieldId', parseInt(e.target.value))}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          >
                            {customFields.map(field => (
                              <option key={`${field.listid}-${field.fieldid}`} value={field.fieldid}>
                                {field.name} (Field {field.fieldid}, List {field.listid})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Values (one per line)
                          </label>
                          <textarea
                            rows={4}
                            value={rule.values.join('\n')}
                            onChange={(e) => updateRule(rule.id, 'values', e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter values, one per line&#10;Example:&#10;Los Angeles, CA&#10;San Francisco, CA&#10;Sacramento, CA"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {rule.values.length} value{rule.values.length !== 1 ? 's' : ''} entered
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {segmentFormData.rules.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Filter className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      {editingSegment ? (
                        <>
                          <p>No rules could be loaded from the segment.</p>
                          <p className="text-sm">You can add rules below.</p>
                        </>
                      ) : (
                        <>
                          <p>No rules defined yet</p>
                          <p className="text-sm">Click &quot;Add Rule&quot; to create your first segment rule</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={closeSegmentEditor}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveSegment}
                  disabled={!segmentFormData.name || segmentFormData.rules.length === 0}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingSegment ? 'Update Segment' : 'Create Segment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Segment Health Check Modal */}
      {healthCheckSegment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-0 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-green-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Segment Health Check</h2>
                  <p className="text-sm text-gray-500">{healthCheckSegment.segmentname}</p>
                </div>
              </div>
              <button onClick={closeHealthCheck} className="text-gray-400 hover:text-gray-600 print:hidden">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-3">
              {loadingHealthCheck ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3 text-green-600" />
                  <p className="text-sm">Counting active subscribers by location…</p>
                  <p className="text-xs text-gray-400 mt-1">This may take a moment for large lists</p>
                </div>
              ) : healthCheckData ? (
                <>
                  {/* List ID badge */}
                  <p className="text-xs text-gray-500 mb-3">
                    List ID: <span className="font-medium text-gray-700">{healthCheckData.listId}</span>
                    &nbsp;·&nbsp;
                    {healthCheckData.breakdown.length} location{healthCheckData.breakdown.length !== 1 ? 's' : ''}
                  </p>

                  {/* Data Table */}
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-semibold text-gray-600 uppercase tracking-wider text-xs">County, State</th>
                          <th className="px-4 py-2 text-right font-semibold text-gray-600 uppercase tracking-wider text-xs">Active Contacts</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {healthCheckData.breakdown.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-1.5 text-gray-800">{row.countyState}</td>
                            <td className="px-4 py-1.5 text-right font-medium text-gray-900">{row.count.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 border-t-2 border-gray-300">
                          <td className="px-4 py-2 font-bold text-gray-900">TOTAL</td>
                          <td className="px-4 py-2 text-right font-bold text-gray-900">{healthCheckData.total.toLocaleString()}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              ) : null}
            </div>

            {/* Modal Footer – actions */}
            {healthCheckData && !loadingHealthCheck && (
              <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-gray-200">
                <button
                  onClick={copyHealthCheck}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  {healthCheckCopied ? (
                    <><CheckCheck className="h-4 w-4 mr-2 text-green-600" />Copied!</>
                  ) : (
                    <><Copy className="h-4 w-4 mr-2" />Copy</>
                  )}
                </button>
                <button
                  onClick={printHealthCheck}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print / Save PDF
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;